import { IdentityAvatar } from '@/heroui/IdentityAvatar';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip } from '@heroui/react';
import { useAuthStore } from '@/stores/authStore';
import { useDataStore } from '@/stores/dataStore';
import { UserCardTrigger } from '@/components/userCard/UserCardTrigger';
import { presenceDotClass, resolveDisplayedStatus } from '@/lib/presence';
import { toggleSelfMute, toggleSelfDeaf } from '@/lib/voiceCommands';
import { speakBlockedIn } from '@/lib/voiceRights';
import { getPreDeaf, getPreMute, setPreDeaf, subscribeVoicePrefs } from '@/lib/voicePrefs';
import { HeadphonesIcon, HeadphonesOffIcon, MicIcon, MicOffIcon, SettingsIcon } from '@/components/icons';
import { platformOwnsIdentity } from '@/lib/platformIdentity';

/**
 * Bottom user widget for the HeroUI sidebar — avatar + name + mic / deafen /
 * settings on real HeroUI Button. Mic & deaf operate on the persisted
 * pre-prefs out of voice and on the live session in voice (voiceCommands).
 * Settings opens the shared UserSettingsModal (mounted by the caller).
 */
export function HeroUserWidget({ onOpenSettings }: { onOpenSettings: () => void }) {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);
  const currentGuildId = useDataStore((s) => s.currentGuildId);
  const presence = useDataStore((s) => (me ? s.presence[me.id] : undefined));
  const myVoiceChannelId = useDataStore((s) => s.myVoiceChannelId);
  // The room withholds SPEAK: the mic is held on the server, shown as locked.
  const speakBlocked = useDataStore(speakBlockedIn);
  const myVoiceState = useDataStore((s) =>
    me && myVoiceChannelId
      ? s.voiceByChannel[myVoiceChannelId]?.find((v) => v.userId === me.id) ?? null
      : null,
  );

  // Bumped on every press: remounts the icon so its shake plays once per click.
  const [micPresses, setMicPresses] = useState(0);
  const [soundPresses, setSoundPresses] = useState(0);
  const [preMute, setPreMuteState] = useState(getPreMute());
  const [preDeaf, setPreDeafState] = useState(getPreDeaf());
  useEffect(
    () =>
      subscribeVoicePrefs(() => {
        setPreMuteState(getPreMute());
        setPreDeafState(getPreDeaf());
      }),
    [],
  );

  if (!me) return null;
  // Inside the platform's Chat frame the shell shows who you are (avatar,
  // name, status); the dock keeps only Bunker's own mic / sound / settings.
  const embedded = platformOwnsIdentity();

  const inVoice = !!myVoiceState;
  const status = resolveDisplayedStatus(me.status, presence, true);
  const label = me.displayName || me.username;
  const muted = inVoice ? myVoiceState!.selfMute || myVoiceState!.serverMute || speakBlocked : preMute;
  const deafened = inVoice ? myVoiceState!.selfDeaf || myVoiceState!.serverDeaf : preDeaf;
  // Sound off stops transmitting too, so the mic shows off while deafened and
  // its press brings both back (voiceCommands.toggleSelfMute).
  const micShownOff = muted || deafened;
  const serverMuted = inVoice && (!!myVoiceState?.serverMute || speakBlocked);
  const lockedLabel = myVoiceState?.serverMute ? t('userWidget.micServerMuted') : t('voice.rights.speakLost');
  const serverDeafened = inVoice && !!myVoiceState?.serverDeaf;
  const micAction = serverMuted
    ? lockedLabel
    : micShownOff ? t('userWidget.micToggleOn') : t('userWidget.micToggleOff');
  const soundAction = serverDeafened
    ? t('userWidget.deafServerMuted')
    : deafened ? t('userWidget.deafToggleOn') : t('userWidget.deafToggleOff');

  const voiceArgs = {
    inVoice,
    selfMuted: myVoiceState?.selfMute ?? false,
    selfDeafened: myVoiceState?.selfDeaf ?? false,
    serverMute: myVoiceState?.serverMute ?? false,
    serverDeaf: myVoiceState?.serverDeaf ?? false,
  };

  return (
    <div className={'studio-user flex items-center gap-2.5 px-2.5 py-2.5' + (embedded ? ' justify-end' : '')}>
      {/* Avatar + name open your own profile panel (status / roles / bio /
          "Полный профиль"). The mic / deaf / settings buttons stay outside
          the trigger so they don't open the card. */}
      {!embedded && (
      <UserCardTrigger
        user={me}
        guildId={currentGuildId}
        onOpenSettings={onOpenSettings}
        label={t('userWidget.myProfile')}
        className="-m-1 flex min-w-0 flex-1 items-center gap-2.5 rounded-lg p-1 text-left transition-colors hover:bg-default/40"
      >
        <span className="relative shrink-0">
          <IdentityAvatar id={me.id} name={label} src={me.avatarUrl} size="md" />
          <span
            className={'absolute -bottom-0.5 -right-0.5 size-3 rounded-full ring-2 ring-surface ' + presenceDotClass(status)}
          />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] font-medium text-foreground">{label}</span>
          <span className="block truncate text-[13px] text-muted">@{me.username}</span>
        </span>
      </UserCardTrigger>
      )}
      <div className="studio-user-actions flex shrink-0 items-center gap-0.5">
        <Tooltip delay={0}>
            <Button
              aria-label={micAction}
              aria-pressed={micShownOff}
              aria-disabled={serverMuted}
              isIconOnly
              size="md"
              variant="ghost"
              onPress={() => {
                setMicPresses((n) => n + 1);
                // While deafened the mic press brings the sound back too.
                if (deafened && !serverDeafened) setSoundPresses((n) => n + 1);
                if (serverMuted) return;
                toggleSelfMute(voiceArgs);
              }}
              className={'group ' + (micShownOff ? 'bg-danger/15 text-danger hover:bg-danger/25 ' : '') + (serverMuted ? 'cursor-not-allowed ' : '')}
            >
              <span key={micPresses} className={pressShakeClass(micPresses)}>
                {micShownOff ? <MicOffIcon className="size-5" /> : <MicIcon className="size-5" />}
              </span>
            </Button>
          <Tooltip.Content>{serverMuted ? lockedLabel : muted ? t('userWidget.micStateOff') : deafened ? t('userWidget.micDeafened') : t('userWidget.micStateOn')}</Tooltip.Content>
        </Tooltip>
        <Tooltip delay={0}>
            <Button
              aria-label={soundAction}
              aria-pressed={deafened}
              aria-disabled={serverDeafened}
              isIconOnly
              size="md"
              variant="ghost"
              onPress={() => {
                // Sound off takes the mic with it, so both icons move.
                setSoundPresses((n) => n + 1);
                setMicPresses((n) => n + 1);
                if (serverDeafened) return;
                if (inVoice) toggleSelfDeaf(voiceArgs);
                else setPreDeaf(!preDeaf);
              }}
              className={'group ' + (deafened ? 'bg-danger/15 text-danger hover:bg-danger/25 ' : '') + (serverDeafened ? 'cursor-not-allowed ' : '')}
            >
              <span key={soundPresses} className={pressShakeClass(soundPresses)}>
                {deafened ? <HeadphonesOffIcon className="size-5" /> : <HeadphonesIcon className="size-5" />}
              </span>
            </Button>
          <Tooltip.Content>{serverDeafened ? t('userWidget.deafServerMuted') : deafened ? t('userWidget.deafStateOff') : t('userWidget.deafStateOn')}</Tooltip.Content>
        </Tooltip>
        <Tooltip delay={0}>
            <Button aria-label={t('common.settings')} data-tour="voice-settings" isIconOnly size="md" variant="ghost" onPress={onOpenSettings} className="group">
              {/* Gear spins on hover, like the React Bits PillNav logo. */}
              <SettingsIcon className="size-5 transition-transform duration-700 ease-out group-hover:rotate-[360deg]" />
            </Button>
          <Tooltip.Content>{t('common.settings')}</Tooltip.Content>
        </Tooltip>
      </div>
    </div>
  );
}

/** Shake once after a press; nothing before the first press. */
function pressShakeClass(presses: number): string {
  return presses > 0 ? 'inline-flex motion-safe:[animation:icon-shake_0.4s_ease-in-out_1]' : 'inline-flex';
}
